import { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdTrendingUp, MdEdit, MdCheck, MdClose } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

interface YieldRow {
    sellerId: string;
    sellerEmail: string;
    sellerName: string;
    commission: number;
    commissionFixedPrice: number | null;
    totalLeads: number;
    totalBuyerRevenue: number;
    totalSellerPayout: number;
    platformMargin: number;
    effectiveMarginPct: number;
    approvedLeads: number;
    rejectedLeads: number;
    acceptRate: number | null;
    yieldScore: number;
    suggestedCommission: number | null;
    suggestedFixedPrice: number | null;
}

const ScoreBadge = ({ score }: { score: number }) => {
    if (score >= 3) return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <MdTrendingUp className="h-3 w-3" /> Candidat ({score}/4)
        </span>
    );
    if (score === 2) return (
        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            À surveiller ({score}/4)
        </span>
    );
    return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500 dark:bg-navy-600 dark:text-gray-400">
            Stable ({score}/4)
        </span>
    );
};

export default function BackOfficeYieldManagement() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<YieldRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterScore, setFilterScore] = useState<'all' | 'candidates' | 'watch'>('all');
    const [editing, setEditing] = useState<{ id: string; field: 'commission' | 'fixed'; value: string } | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/yield-management`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRows(response.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const applyRate = async (sellerId: string) => {
        if (!editing) return;
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload: { commission?: number; commissionFixedPrice: number | null } = editing.field === 'commission'
                ? { commission: parseFloat(editing.value), commissionFixedPrice: null }
                : { commissionFixedPrice: parseFloat(editing.value) };
            await axios.patch(`${CONFIG.apiUrl}/admin/users/${sellerId}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEditing(null);
            fetchData();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const applySuggested = async (row: YieldRow) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload: { commission?: number | null; commissionFixedPrice: number | null } = row.suggestedFixedPrice != null
                ? { commissionFixedPrice: row.suggestedFixedPrice }
                : { commission: row.suggestedCommission, commissionFixedPrice: null };
            await axios.patch(`${CONFIG.apiUrl}/admin/users/${row.sellerId}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchData();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const filtered = rows.filter((r) => {
        if (filterScore === 'candidates') return r.yieldScore >= 3;
        if (filterScore === 'watch') return r.yieldScore === 2;
        return true;
    });

    const totalPlatformMargin = rows.reduce((s, r) => s + r.platformMargin, 0);
    const candidates = rows.filter((r) => r.yieldScore >= 3);
    const estimatedUplift = candidates.reduce((s, r) => {
        if (r.suggestedCommission != null) {
            const delta = (r.suggestedCommission - r.commission) / 100;
            return s + r.totalSellerPayout * delta;
        }
        if (r.suggestedFixedPrice != null) {
            return s + (r.suggestedFixedPrice - r.commissionFixedPrice!) * r.totalLeads;
        }
        return s;
    }, 0);

    return (
        <div className="mt-5 space-y-5">
            {/* KPI row */}
            <div className="grid grid-cols-3 gap-4">
                <Card extra="p-5 flex flex-col gap-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Marge plateforme (30j)</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPlatformMargin.toFixed(0)}€</p>
                </Card>
                <Card extra="p-5 flex flex-col gap-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Vendeurs candidats</p>
                    <p className="text-2xl font-bold text-green-600">{candidates.length}</p>
                </Card>
                <Card extra="p-5 flex flex-col gap-1">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Uplift estimé si appliqué</p>
                    <p className="text-2xl font-bold text-green-600">+{estimatedUplift.toFixed(0)}€</p>
                    <p className="text-xs text-gray-400">sur les 30 derniers jours</p>
                </Card>
            </div>

            <Card extra="p-5">
                {/* Header + filters */}
                <div className="mb-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Yield management</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Optimisation des commissions vendeurs — 30 derniers jours
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {(['all', 'candidates', 'watch'] as const).map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilterScore(f)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                    filterScore === f
                                        ? 'bg-brand-500 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300'
                                }`}
                            >
                                {f === 'all' ? 'Tous' : f === 'candidates' ? 'Candidats' : 'À surveiller'}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <p className="py-10 text-center text-sm text-gray-400">Chargement…</p>
                ) : filtered.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-400">Aucun vendeur avec des leads sur 30 jours.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                                    <th className="pb-3 text-left">Vendeur</th>
                                    <th className="pb-3 text-right">Commission actuelle</th>
                                    <th className="pb-3 text-right">Leads</th>
                                    <th className="pb-3 text-right">Rev. acheteur</th>
                                    <th className="pb-3 text-right">Marge plateforme</th>
                                    <th className="pb-3 text-right">Marge eff.</th>
                                    <th className="pb-3 text-right">Taux accept.</th>
                                    <th className="pb-3 text-center">Score</th>
                                    <th className="pb-3 text-center">Suggestion</th>
                                    <th className="pb-3 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-navy-700">
                                {filtered.map((row) => {
                                    const isEditing = editing?.id === row.sellerId;
                                    return (
                                        <tr key={row.sellerId} className="hover:bg-gray-50/50 dark:hover:bg-navy-700/30">
                                            {/* Seller */}
                                            <td className="py-3 pr-4">
                                                <button
                                                    onClick={() => navigate(`/back-office/users/edit/${row.sellerId}`)}
                                                    className="text-left hover:underline"
                                                >
                                                    <p className="font-medium text-gray-900 dark:text-white">{row.sellerName.trim() || '—'}</p>
                                                    <p className="text-xs text-gray-400">{row.sellerEmail}</p>
                                                </button>
                                            </td>

                                            {/* Current commission */}
                                            <td className="py-3 pr-4 text-right">
                                                {isEditing ? (
                                                    <div className="flex items-center justify-end gap-1">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.1"
                                                            value={editing.value}
                                                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                                                            className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm dark:border-navy-500 dark:bg-navy-800"
                                                        />
                                                        <span className="text-xs text-gray-400">{editing.field === 'commission' ? '%' : '€ fixe'}</span>
                                                        <button onClick={() => applyRate(row.sellerId)} disabled={saving} className="text-green-500 hover:text-green-600"><MdCheck /></button>
                                                        <button onClick={() => setEditing(null)} className="text-red-400 hover:text-red-500"><MdClose /></button>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-end gap-2">
                                                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                            row.commissionFixedPrice != null
                                                                ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                        }`}>
                                                            {row.commissionFixedPrice != null
                                                                ? `${row.commissionFixedPrice}€ fixe`
                                                                : `${row.commission}%`}
                                                        </span>
                                                        <button
                                                            onClick={() => setEditing({
                                                                id: row.sellerId,
                                                                field: row.commissionFixedPrice != null ? 'fixed' : 'commission',
                                                                value: String(row.commissionFixedPrice ?? row.commission),
                                                            })}
                                                            className="text-gray-400 hover:text-gray-600"
                                                        >
                                                            <MdEdit className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Leads */}
                                            <td className="py-3 pr-4 text-right font-medium text-gray-700 dark:text-gray-200">{row.totalLeads}</td>

                                            {/* Buyer revenue */}
                                            <td className="py-3 pr-4 text-right text-gray-600 dark:text-gray-300">{row.totalBuyerRevenue.toFixed(0)}€</td>

                                            {/* Platform margin */}
                                            <td className="py-3 pr-4 text-right font-semibold text-gray-900 dark:text-white">{row.platformMargin.toFixed(0)}€</td>

                                            {/* Effective margin % */}
                                            <td className="py-3 pr-4 text-right">
                                                <span className={`font-semibold ${row.effectiveMarginPct < 15 ? 'text-orange-500' : 'text-gray-700 dark:text-gray-200'}`}>
                                                    {row.effectiveMarginPct.toFixed(1)}%
                                                </span>
                                            </td>

                                            {/* Accept rate */}
                                            <td className="py-3 pr-4 text-right">
                                                {row.acceptRate != null ? (
                                                    <span className={`font-semibold ${row.acceptRate > 85 ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'}`}>
                                                        {row.acceptRate.toFixed(0)}%
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                                <p className="text-[10px] text-gray-400">{row.approvedLeads}✓ {row.rejectedLeads}✗</p>
                                            </td>

                                            {/* Score */}
                                            <td className="py-3 pr-4 text-center">
                                                <ScoreBadge score={row.yieldScore} />
                                            </td>

                                            {/* Suggestion */}
                                            <td className="py-3 pr-4 text-center">
                                                {row.suggestedCommission != null && (
                                                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/20 dark:text-green-400">
                                                        → {row.suggestedCommission}%
                                                    </span>
                                                )}
                                                {row.suggestedFixedPrice != null && (
                                                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/20 dark:text-green-400">
                                                        → {row.suggestedFixedPrice}€ fixe
                                                    </span>
                                                )}
                                                {row.suggestedCommission == null && row.suggestedFixedPrice == null && (
                                                    <span className="text-gray-300">—</span>
                                                )}
                                            </td>

                                            {/* Actions */}
                                            <td className="py-3 text-center">
                                                {(row.suggestedCommission != null || row.suggestedFixedPrice != null) && (
                                                    <button
                                                        onClick={() => applySuggested(row)}
                                                        disabled={saving}
                                                        className="rounded-lg bg-green-500 px-3 py-1 text-xs font-semibold text-white transition hover:bg-green-600 disabled:opacity-50"
                                                    >
                                                        Appliquer
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
